// Recipe Builder rb-2 — the web client for /api/recipes (rb-1). Every call needs `recipes.manage` on the server
// (Owner, General Manager, Executive Chef, Bar Manager by default). Spec docs/specs/M3-recipe-builder-pricing-scope.md.
// rb-3 adds costing (each line's cost from inventory) and price (targets, suggested price, the chef's price).
// rb-4 adds the over-target list (items whose cost % has drifted past the % they are priced at) and management's margin.
import type { ApiClient } from './client'
import { round2 } from '../lib/money-round'

export const RECIPE_UNITS = ['EACH', 'CASE', 'LB', 'OZ', 'GALLON', 'LITER', 'BOTTLE', 'BAG', 'BOX', 'BUNCH', 'PORTION'] as const
export type RecipeUnit = (typeof RECIPE_UNITS)[number]

/** A recipe belongs to a menu item or to a prep item. */
export type RecipeOwnerKind = 'menu' | 'prep'

export interface MenuRecipeRow {
  id: string
  name: string
  category: string
  isActive: boolean
  group: string | null
  recipe: { lineCount: number; warningCount: number; updatedAt: string } | null
  // rb-3
  plateCost: number | null
  plateComplete: boolean | null
  basePrice: number | null
  costPct: number | null
  pricingPct: number | null
  suggestedPrice: number | null
}

/** rb-3 — an ingredient's cost, as the server works it out from inventory. Null fields are unknown, never zero. */
export interface IngredientCost {
  perRecipeUnit: number | null
  perStockUnit: number | null
  stockUnit: string | null
  recipeUnit: string | null
  /** rb-7: 'batch28' — a tracked prep costed from its batches in the window (receipts = the batch count). */
  basis: 'avg28' | 'last' | 'recipe' | 'batch28' | null
  receipts: number
  /** rb-7: a prep's cost from its recipe, shown beside its batches' average. */
  estimatePerStock?: number | null
  lastPaidPerStock: number | null
  priceFlag: boolean
  complete: boolean
  problem: string | null
  lineCost: number | null
}

export interface ComponentOption {
  kind: 'product' | 'prep'
  id: string
  name: string
  category?: string
  stockUnit: string | null
  recipeUnit: string | null
  recipePerStock: number | null
  yieldPct?: number | null
  cost?: IngredientCost
}

export interface RecipeLineView {
  id: string
  sortOrder: number
  qtyPerPortion: number
  yieldPct: number | null
  depletionLocationId: string | null
  component: {
    kind: 'product' | 'prep'
    id: string
    name: string
    recipeUnit: string | null
    stockUnit: string | null
    recipePerStock: number | null
    batchYieldQty?: number | null
    yieldPct?: number | null
  }
  warnings: string[]
  cost?: IngredientCost
}

export type Sourced = { value: number | null; source: 'item' | 'class' | 'entered' | 'measured' | null }

export interface PricingPanel {
  class: 'food' | 'bar' | 'nonAlc' | null
  classLabel: string | null
  target: Sourced
  waste: Sourced
  q: Sourced
  pricingPct: number | null
  suggestedPrice: number | null
  basePrice: number | null
  actualCostPct: number | null
  margin: number | null
  problem: string | null
  itemTargetPct: number | null
  itemWastePct: number | null
  // rb-5 (optional so an older server's answer still renders)
  itemQPct?: number | null
  qPeriodName?: string | null
  qProblem?: string | null
}

export interface RecipeView {
  owner: { kind: RecipeOwnerKind; id: string; name: string }
  recipe: { id: string; notes: string | null; updatedAt: string; lines: RecipeLineView[] } | null
  // rb-3 (optional so an older server's answer still renders)
  costing?: { total: number; complete: boolean; windowDays: number; windowStart: string; windowEnd: string; priceFlagPct: number | null }
  canManage?: boolean
  pricing?: PricingPanel
  batch?: { batchYieldQty: number | null; stockUnit: string | null; recipeUnit: string | null; recipePerStock: number | null; perStockUnit: number | null; perRecipeUnit: number | null; complete: boolean; problem: string | null }
  priceChanged?: boolean
}

export interface PriceChange {
  id: string
  at: string
  who: string
  source: 'recipe' | 'menu'
  oldPrice: number | null
  newPrice: number | null
  plateCost: number | null
  plateComplete: boolean | null
  pricingPct: number | null
  suggestedPrice: number | null
}

export const SETTINGS_FIELDS = [
  'targetFoodPct', 'targetBarPct', 'targetBeveragePct',
  'wasteFoodPct', 'wasteBarPct', 'wasteBeveragePct',
  'qFoodPct', 'qBarPct', 'qBeveragePct',
  'priceFlagPct',
  'overTargetMarginPct',
] as const
export type SettingsField = (typeof SETTINGS_FIELDS)[number]
export type PricingClassKey = 'food' | 'bar' | 'nonAlc'
export type QSource = 'ENTERED' | 'MEASURED'
export const Q_SOURCE_FIELDS: Record<PricingClassKey, 'qFoodSource' | 'qBarSource' | 'qBeverageSource'> = { food: 'qFoodSource', bar: 'qBarSource', nonAlc: 'qBeverageSource' }
export interface QMeasuredClass { pct: number | null; perCover: number | null; problem: string | null }
export type PricingSettings = Record<SettingsField, number | null> & {
  pricingPct: { food: number | null; bar: number | null; nonAlc: number | null }
  canManage: boolean
  // rb-5 (optional so an older server's answer still renders)
  qSource?: Record<PricingClassKey, QSource>
  measured?: ({ period: { id: string; name: string; startKey: string; endKey: string } | null } & Record<PricingClassKey, QMeasuredClass>) | null
}

// ── rb-5: the Q-factor report ──
export interface QProductUsage {
  productId: string; name: string; cls: PricingClassKey
  opening: number | null; purchases: number; closing: number | null; reclassed: number
  /** DEF-149: rows with no cost known, not in the dollars. */
  unvaluedRows?: number
  usage: number | null; problem: string | null
}
export interface QClassMeasure {
  cls: PricingClassKey; label: string; products: QProductUsage[]
  usage: number | null; classSales: number; pct: number | null; perCover: number | null; problem: string | null
}
export interface QFactorReport {
  measured: { period: { id: string; name: string; startKey: string; endKey: string } | null; covers: number; problem: string | null; classes: Record<PricingClassKey, QClassMeasure> }
  entered: Record<PricingClassKey, number | null>
  qSource: Record<PricingClassKey, QSource>
  qInUse: Record<PricingClassKey, { source: QSource; value: number | null; problem: string | null; periodName: string | null }>
  products: Array<{ id: string; name: string; cls: PricingClassKey | null; isActive: boolean; isQFactor: boolean; recipeLines: number }>
  periods: Array<{ id: string; name: string }>
  canManage: boolean
}
export async function fetchQFactor(api: ApiClient, periodId?: string): Promise<QFactorReport> {
  return (await api.get<{ data: QFactorReport }>(`/recipes/q-factor${periodId ? `?periodId=${encodeURIComponent(periodId)}` : ''}`)).data
}
// Recipe Builder rb-10b — actual vs theoretical (Thomas 2026-10-16).
export type AvtClassKey = 'food' | 'nonAlc' | 'spirits' | 'beer' | 'wine' | 'mixes'
export interface AvtClassSummary {
  cls: AvtClassKey | 'bar'; label: string; sales: number; actual: number; theoretical: number; variance: number
  explained: number; byCause: Record<string, number>; unexplained: number; unexplainedPts: number | null
  actualPct: number | null; theoreticalPct: number | null; unvaluedRows: number; products: number
}
export interface AvtProductRow {
  productId: string; name: string; cls: AvtClassKey; actual: number; theoretical: number; explained: number
  byCause: Record<string, number>; unexplained: number; unvaluedRows: number; notCounted: Array<'opening' | 'closing'>
}
export interface ActualVsTheoretical {
  report: {
    period: { id: string; name: string; startKey: string; endKey: string } | null
    problem: string | null
    classes: AvtClassSummary[]
    products: AvtProductRow[]
    notCounted: Array<{ productId: string; name: string; which: Array<'opening' | 'closing'> }>
  }
  trend: Array<{ periodId: string; name: string; problem: string | null; pts: Partial<Record<AvtClassKey | 'bar', number | null>> }>
  periods: Array<{ id: string; name: string; startKey: string; endKey: string }>
}
export interface AvtDrillItem { menuItemId: string; name: string; unitsSold: number; theoreticalQty: number }
export async function fetchActualVsTheoretical(api: ApiClient, periodId?: string): Promise<ActualVsTheoretical> {
  return (await api.get<{ data: ActualVsTheoretical }>(`/recipes/actual-vs-theoretical${periodId ? `?periodId=${encodeURIComponent(periodId)}` : ''}`)).data
}
export async function fetchAvtDrillDown(api: ApiClient, productId: string, periodId: string): Promise<{ product: { id: string; name: string }; items: AvtDrillItem[] }> {
  return (await api.get<{ data: { product: { id: string; name: string }; items: AvtDrillItem[] } }>(`/recipes/actual-vs-theoretical/products/${encodeURIComponent(productId)}?periodId=${encodeURIComponent(periodId)}`)).data
}
export const CAUSE_LABEL: Record<string, string> = {
  SPOILAGE: 'Spoilage', OVERPRODUCTION: 'Overproduction', BREAKAGE: 'Breakage', EMPLOYEE_MEAL: 'Staff meals', RECIPE_ERROR: 'Recipe error',
  THEFT: 'Theft (recorded)', COMP: 'Comped stock', SPILL: 'Spill', VOID_AFTER_MAKE: 'Void after made', SAMPLE: 'Sample',
  SHRINK: 'Shrink (recorded)', CORRECTION: 'Correction', OTHER: 'Other waste', MADE_INTO_PREPS: 'Made into preps',
}

export async function setQFactorProduct(api: ApiClient, productId: string, isQFactor: boolean): Promise<{ id: string; name: string; isQFactor: boolean; changed: boolean }> {
  return (await api.patch<{ data: { id: string; name: string; isQFactor: boolean; changed: boolean } }>(`/recipes/q-factor/products/${encodeURIComponent(productId)}`, { isQFactor })).data
}

/** One line as the editor sends it: exactly one of productId / prepItemId. */
export interface RecipeLineInput {
  productId: string | null
  prepItemId: string | null
  qtyPerPortion: number
  yieldPct: number | null
  depletionLocationId: string | null
}

export interface PrepItem {
  id: string
  name: string
  stockUnit: string | null
  recipeUnit: string | null
  recipePerStock: number | null
  batchYieldQty: number | null
  yieldPct: number | null
  homeLocationId: string | null
  isActive: boolean
  hasRecipe: boolean
  /** rb-7: produced and counted (Inventory → Production). */
  tracked?: boolean
}

export interface PrepItemPayload {
  name?: string
  stockUnit?: string | null
  recipeUnit?: string | null
  recipePerStock?: number | null
  batchYieldQty?: number | null
  yieldPct?: number | null
  homeLocationId?: string | null
  isActive?: boolean
}

const recipePath = (kind: RecipeOwnerKind, id: string) =>
  kind === 'menu' ? `/recipes/menu-items/${encodeURIComponent(id)}` : `/recipes/prep-items/${encodeURIComponent(id)}/recipe`

export async function fetchMenuRecipeList(api: ApiClient): Promise<MenuRecipeRow[]> {
  return (await api.get<{ data: MenuRecipeRow[] }>('/recipes/menu-items')).data
}

export async function fetchComponents(api: ApiClient, q: string): Promise<ComponentOption[]> {
  const res = await api.get<{ data: { products: ComponentOption[]; prepItems: ComponentOption[] } }>(`/recipes/components?q=${encodeURIComponent(q)}`)
  return [...res.data.prepItems, ...res.data.products]
}

export async function fetchPrepItems(api: ApiClient, includeInactive = false): Promise<PrepItem[]> {
  return (await api.get<{ data: PrepItem[] }>(`/recipes/prep-items${includeInactive ? '?includeInactive=1' : ''}`)).data
}

export async function createPrepItem(api: ApiClient, payload: PrepItemPayload): Promise<PrepItem> {
  return (await api.post<{ data: PrepItem }>('/recipes/prep-items', payload)).data
}

export async function updatePrepItem(api: ApiClient, id: string, payload: PrepItemPayload): Promise<PrepItem> {
  return (await api.patch<{ data: PrepItem }>(`/recipes/prep-items/${encodeURIComponent(id)}`, payload)).data
}

export async function fetchRecipe(api: ApiClient, kind: RecipeOwnerKind, id: string): Promise<RecipeView> {
  return (await api.get<{ data: RecipeView }>(recipePath(kind, id))).data
}

export async function saveRecipe(api: ApiClient, kind: RecipeOwnerKind, id: string, body: { notes: string | null; lines: RecipeLineInput[] }): Promise<RecipeView> {
  return (await api.put<{ data: RecipeView }>(recipePath(kind, id), body)).data
}

export async function deleteRecipe(api: ApiClient, kind: RecipeOwnerKind, id: string): Promise<void> {
  await api.delete(recipePath(kind, id))
}

// ── rb-3: price and targets ──

export async function savePrice(api: ApiClient, menuItemId: string, price: number): Promise<RecipeView> {
  return (await api.put<{ data: RecipeView }>(`/recipes/menu-items/${encodeURIComponent(menuItemId)}/price`, { price })).data
}

export async function fetchPriceHistory(api: ApiClient, menuItemId: string): Promise<PriceChange[]> {
  return (await api.get<{ data: PriceChange[] }>(`/recipes/menu-items/${encodeURIComponent(menuItemId)}/price-history`)).data
}

export async function updateItemPricing(api: ApiClient, menuItemId: string, body: { targetCostPct?: number | null; wasteBudgetPct?: number | null; qFactorPct?: number | null }): Promise<RecipeView> {
  return (await api.patch<{ data: RecipeView }>(`/recipes/menu-items/${encodeURIComponent(menuItemId)}/pricing`, body)).data
}

/** rb-4 — an active item whose cost % at today's costs runs past its price-at % by more than management's margin. */
export interface OverTargetRow {
  id: string
  name: string
  group: string | null
  classLabel: string | null
  plateCost: number
  /** false: some ingredients have no cost yet — the true cost % is higher still */
  plateComplete: boolean
  basePrice: number | null
  costPct: number | null
  pricingPct: number | null
  /** cost % − pricing %, in points */
  overBy: number | null
  suggestedPrice: number | null
  /** suggested − current price */
  priceChange: number | null
}
export interface OverTargetList {
  /** management's margin in points; null = any amount over */
  marginPct: number | null
  windowDays: number
  over: OverTargetRow[]
  /** active items checked and within target */
  within: number
  /** items that can't be judged yet, with the reason */
  unchecked: Array<{ id: string; name: string; group: string | null; classLabel: string | null; reason: string }>
}

export async function fetchOverTarget(api: ApiClient): Promise<OverTargetList> {
  return (await api.get<{ data: OverTargetList }>('/recipes/over-target')).data
}

/** rb-8 — menu engineering (Thomas 2026-10-14: 70% of an equal share; within each menu section; margin = price − today's
 *  plate cost; plain-word quadrants). */
export type Quadrant = 'POPULAR_PROFITABLE' | 'POPULAR_LOW_MARGIN' | 'PROFITABLE_SLOW' | 'SLOW_LOW_MARGIN'
export interface EngineeredItem {
  id: string
  name: string
  classLabel: string | null
  basePrice: number | null
  plateCost: number | null
  /** false: some ingredients have no cost yet — the plate cost is at least what is shown */
  plateComplete: boolean
  /** units guests paid for (Thomas: paid only) */
  units: number
  /** units comped — shown beside, not counted as demand */
  comped: number
  /** % of the section's units */
  mixPct: number | null
  /** price − today's plate cost */
  margin: number | null
  marginTotal: number | null
  popular: boolean | null
  highMargin: boolean | null
  quadrant: Quadrant | null
  quadrantLabel: string | null
  /** why the item isn't placed */
  reason: string | null
}
export interface EngineeredSection {
  id: string | null
  name: string
  itemCount: number
  totalUnits: number
  popularityLinePct: number | null
  avgMargin: number | null
  marginTotal: number | null
  /** the units the margin line and the margin earned are over: those of the items whose margin is known */
  costedUnits: number
  items: EngineeredItem[]
}
export interface MenuEngineering {
  windowDays: number
  startKey: string
  endKey: string
  businessDays: number
  popularityFactor: number
  guests: number
  guestChecks: number
  /** item sales before comps and discounts */
  itemSales: number
  salesPerGuest: number | null
  quadrants: Record<Quadrant, string>
  advice: Record<Quadrant, string>
  sections: EngineeredSection[]
}

export async function fetchMenuEngineering(api: ApiClient): Promise<MenuEngineering> {
  return (await api.get<{ data: MenuEngineering }>('/recipes/menu-engineering')).data
}

export async function fetchPricingSettings(api: ApiClient): Promise<PricingSettings> {
  return (await api.get<{ data: PricingSettings }>('/recipes/pricing-settings')).data
}

export async function updatePricingSettings(api: ApiClient, body: Partial<Record<SettingsField, number | null>> & Partial<Record<'qFoodSource' | 'qBarSource' | 'qBeverageSource', QSource>>): Promise<PricingSettings> {
  return (await api.patch<{ data: PricingSettings }>('/recipes/pricing-settings', body)).data
}

// ── rb-3: the page's own arithmetic (the server does the same; the page recomputes as the chef types) ──

/** qty ÷ yield × cost per recipe unit — the line's yield, else the ingredient's, else 100 %. Null when unknown. */
export function lineCostOf(qty: number, yieldPct: number | null, componentYieldPct: number | null, perRecipeUnit: number | null): number | null {
  if (perRecipeUnit === null || !Number.isFinite(qty) || qty <= 0) return null
  const y = (yieldPct ?? componentYieldPct ?? 100) / 100
  if (!(y > 0)) return null
  return (qty / y) * perRecipeUnit
}

/** plate ÷ pricing %, to the cent; null when there is no pricing % above zero. */
export function suggestedPriceOf(plate: number, pricingPct: number | null): number | null {
  if (pricingPct === null || !(pricingPct > 0)) return null
  return round2(plate / (pricingPct / 100))
}

export const money = (n: number | null | undefined, digits = 2) =>
  n === null || n === undefined ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits })
export const pct = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`)

// ── rb-10c-1 — variance thresholds, the period alert, the push to staff (spec 0.24 §rb-10c-1; Thomas 2026-10-30) ──
export type AvtLineClass = 'food' | 'nonAlc' | 'spirits' | 'beer' | 'wine' | 'mixes'
export interface VarianceThreshold { line: string; cls: AvtLineClass; label: string; points: number; isDefault: boolean; updatedAt: string | null; updatedByUserId: string | null }
export interface VarianceAlert {
  id: string; line: string; label: string; direction: 'OVER' | 'UNDER'; unexplained: number; pts: number; sales: number; thresholdPts: number
  period: { id: string; name: string }; createdAt: string; acknowledgedAt: string | null; acknowledgedByUserId: string | null
}
export interface PushedLine { cls: AvtLineClass; label: string; unexplained: number; pts: number | null; threshold: number; status: 'OVER' | 'UNDER' | 'WITHIN' | 'NO_SALES' }
export interface PushSnapshot { period: { id: string; name: string; startKey: string; endKey: string } | null; lines: PushedLine[] }
export interface PushedReport { id: string; report: string; pushedAt: string; pushedBy: string | null; snapshot: PushSnapshot }

export async function fetchVarianceThresholds(api: ApiClient): Promise<{ thresholds: VarianceThreshold[]; canManage: boolean }> {
  const r = await api.get<{ data: VarianceThreshold[]; canManage: boolean }>('/recipes/variance-thresholds')
  return { thresholds: r.data, canManage: r.canManage }
}
export async function saveVarianceThresholds(api: ApiClient, thresholds: Partial<Record<AvtLineClass, number>>): Promise<VarianceThreshold[]> {
  return (await api.patch<{ data: VarianceThreshold[] }>('/recipes/variance-thresholds', { thresholds })).data
}
export async function fetchVarianceAlerts(api: ApiClient): Promise<VarianceAlert[]> {
  return (await api.get<{ data: VarianceAlert[] }>('/recipes/variance-alerts')).data
}
export async function acknowledgeVarianceAlert(api: ApiClient, id: string): Promise<VarianceAlert> {
  return (await api.post<{ data: VarianceAlert }>(`/recipes/variance-alerts/${encodeURIComponent(id)}/acknowledge`, {})).data
}
export async function pushAvtReport(api: ApiClient, periodId: string): Promise<{ id: string; messageId: string; snapshot: PushSnapshot }> {
  return (await api.post<{ data: { id: string; messageId: string; snapshot: PushSnapshot } }>(`/recipes/actual-vs-theoretical/${encodeURIComponent(periodId)}/push`, {})).data
}
export async function fetchPushedReport(api: ApiClient, id: string): Promise<PushedReport> {
  return (await api.get<{ data: PushedReport }>(`/recipes/pushed-reports/${encodeURIComponent(id)}`)).data
}
/** Pure: a line's place against its threshold, as the API decides it (more than it, read to the cent of a point). */
export function thresholdStatus(pts: number | null, threshold: number): 'OVER' | 'UNDER' | 'WITHIN' | 'NO_SALES' {
  if (pts === null) return 'NO_SALES'
  if (Math.round(Math.abs(pts) * 100) <= Math.round(threshold * 100)) return 'WITHIN'
  return pts > 0 ? 'OVER' : 'UNDER'
}
export const STATUS_WORD: Record<'OVER' | 'UNDER' | 'WITHIN' | 'NO_SALES', string> = { OVER: 'Over', UNDER: 'Under', WITHIN: 'Within', NO_SALES: 'No sales' }
